import config from 'constants/config'
import { getExperienceSuggestedSearches, type ExperienceSuggestedSearch, type ExperienceSuggestedSearches } from 'api/experiences'
import {
  trackExperienceSuggestedSearchClick,
  trackExperienceSuggestedSearchesImpression,
  type ExperienceSuggestedSearchBucket,
} from 'analytics/snowplow/events/experienceSuggestedSearchEvents'
import Group from 'components/utils/Group'
import { take } from 'lib/array/arrayUtils'
import LayoutContainer from 'components/Common/LayoutContainer'
import GeoContext from 'contexts/geoContext'
import { formatRegionName } from 'lib/format/formatRegionName'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import uuidV4 from 'lib/string/uuidV4Utils'

const Container = styled(LayoutContainer)`
  padding-block: ${rem(40)};
`

const SearchGroup = styled.div`
  margin-block-start: ${rem(32)};
`

const Chips = styled(Group)`
  margin-block-start: ${rem(16)};
`

const SearchChip = styled(AssistChip)`
  max-width: 100%;
  min-height: ${rem(32)};
  white-space: normal;

  &.size-medium {
    height: auto;
    padding-block: ${rem(4)};
  }

  overflow-wrap: anywhere;
`

interface SearchGroupProps {
  bucket: ExperienceSuggestedSearchBucket
  countryCode?: string
  countryName?: string
  searches: Array<ExperienceSuggestedSearch>
}

function SuggestedSearchGroup({ bucket, countryCode, countryName, searches }: SearchGroupProps) {
  const intl = useIntl()
  const { ref, inView } = useInView({ threshold: 0.5 })
  const impressionKey = `${bucket}_${countryCode ?? 'unknown'}`
  const impressedCountry = useRef('')
  const listInstance = useMemo(uuidV4, [bucket, countryCode])

  useEffect(() => {
    if (inView && impressedCountry.current !== impressionKey) {
      impressedCountry.current = impressionKey
      trackExperienceSuggestedSearchesImpression(bucket, countryCode, listInstance)
    }
  }, [inView, bucket, countryCode, impressionKey, listInstance])

  const handleChipClick = useCallback((event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const position = Number(event.currentTarget.dataset.position)
    trackExperienceSuggestedSearchClick(bucket, countryCode, listInstance, position)
  }, [bucket, countryCode, listInstance])

  const title = bucket === 'local' ? intl.formatMessage({
    id: 'ClLjuRC5N-qpVv04VGCW',
    defaultMessage: 'What others are searching for in {countryName}',
    description: 'Heading above popular Experience searches made by customers in the current visitor country on an offer page',
  }, { countryName }) : intl.formatMessage({
    id: 'pQbycKAykpxDE3Gb6Tu0',
    defaultMessage: 'Trending searches on {brandName}',
    description: 'Heading above worldwide Experience search suggestions on an Experience offer page',
  }, { brandName: config.title })

  return <SearchGroup ref={ref}>
    <Heading as="h4" variant="heading3">{title}</Heading>
    <Chips direction="horizontal" wrap="wrap" gap={12}>
      {searches.map((search, index) => <SearchChip
        key={search.url}
        variant="outline"
        size="medium"
        to={search.url}
        data-position={index + 1}
        onClick={handleChipClick}
      >{intl.formatMessage({
          id: 'trv_T4xDzyCofY354No5',
          defaultMessage: '{position, number}. {searchText}',
          description: 'Numbered Experience search suggestion chip showing its ranking and catalogue label',
        }, { position: index + 1, searchText: search.text })}
      </SearchChip>)}
    </Chips>
  </SearchGroup>
}

function ExperienceOfferPageSuggestedSearches() {
  const { geoCountryCode, currentRegionCode } = useContext(GeoContext)
  const countryCode = geoCountryCode?.toUpperCase() || undefined
  const intl = useIntl()
  const [response, setResponse] = useState<{ countryCode?: string, region: string, searches: ExperienceSuggestedSearches }>()

  useEffect(() => {
    let active = true
    setResponse(undefined)
    getExperienceSuggestedSearches(countryCode, currentRegionCode).then(result => {
      if (active) setResponse({ countryCode, region: currentRegionCode, searches: result })
    }).catch(() => {
      if (active) setResponse(undefined)
    })
    return () => { active = false }
  }, [countryCode, currentRegionCode])

  const searches = response?.countryCode === countryCode && response?.region === currentRegionCode ? response?.searches : undefined
  const local = countryCode ? take(searches?.local ?? [], 8) : []
  const worldwide = take(searches?.worldwide ?? [], 8)
  if (local.length === 0 && worldwide.length === 0) return null

  const countryName = countryCode ? formatRegionName(countryCode, intl.locale) : undefined

  return <Container variant="medium">
    <Heading as="h3" variant="heading2">{intl.formatMessage({
      id: 'r8bJDUCxkLMKcpdicpNR',
      defaultMessage: 'Explore more on {brandName}',
      description: 'Main heading for suggested Experience searches below the carousels on an Experience offer page',
    }, { brandName: config.title })}
    </Heading>
    {local.length > 0 && <SuggestedSearchGroup key={`local_${currentRegionCode}`} bucket="local" countryCode={countryCode} countryName={countryName} searches={local} />}
    {worldwide.length > 0 && <SuggestedSearchGroup key={`worldwide_${currentRegionCode}`} bucket="worldwide" countryCode={countryCode} searches={worldwide} />}
  </Container>
}

export default ExperienceOfferPageSuggestedSearches
